import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import {
  activeCue, attachCaptions, detachCaptions, lineText, normStyle, readCaptionStyle, readCaptionsPref, writeCaptionStyle, writeCaptionsPref,
} from "../lib/captions";

// The player's captions. Give it the player (usePlayer()); it asks the server for the current song's timed lines and tells
// the CC button whether to show (available), whether captions are on, and what pressing it does. `cues` are the lines
// (for the caption bar) and `style` / `setStyle` are how the bar looks on this device. A song with no captions, a Premium
// song's preview clip (guests and free members) and a song still locked behind the 18+ check have none, so the button
// stays out of sight for them.
//
// Who decides whether they are on: once the CC button has been pressed on a device, that choice. Until then the device
// decides, so a phone or tablet with its own captions setting turned on shows them without the button being pressed.
export function useCaptions(p) {
  const id = p.current && p.current.id;
  const preview = !!(p.current && (p.current.locked || p.current.age_locked || p.current.local));   // (a file from the member's own device has no captions on the site)
  const [got, setGot] = useState({ id: null, cues: null });
  const [deviceOn, setDeviceOn] = useState(false);
  const [pref, setPref] = useState(() => readCaptionsPref());
  const [style, setStyleState] = useState(() => readCaptionStyle());

  useEffect(() => {
    if (!id || preview) return undefined;
    let dead = false;
    api.get(`/videos/${id}/captions`)
      .then((r) => {
        const cues = r.data && r.data.cues;
        if (!dead && Array.isArray(cues) && cues.length) setGot({ id, cues });
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [id, preview]);

  // The lines belong to the song they were asked for: when the next song starts they are not used for it.
  const cues = useMemo(() => {
    if (preview || got.id !== id || !got.cues) return null;
    return got.cues.slice().sort((a, b) => a.s - b.s);
  }, [got, id, preview]);

  useEffect(() => {
    const video = p.videoRef && p.videoRef.current;
    if (!video) return undefined;
    setDeviceOn(false);
    attachCaptions(video, cues, () => setDeviceOn(true));
    return () => detachCaptions(video);
  }, [cues, p.videoRef]);

  const on = !!cues && (pref !== null ? pref : deviceOn);
  const onRef = useRef(false);
  onRef.current = on;

  const toggle = useCallback(() => {
    const next = !onRef.current;
    writeCaptionsPref(next);
    setPref(next);
  }, []);

  const setStyle = useCallback((patch) => {
    setStyleState((old) => {
      const next = normStyle({ ...old, ...patch });
      writeCaptionStyle(next);
      return next;
    });
  }, []);

  return { available: !!cues, on, toggle, cues, style, setStyle };
}

// The words to show right now: the line that is on at the video's current time ("" between lines). It follows the
// picture while the song plays and jumps when the person seeks.
export function useActiveLine(videoRef, cues, on) {
  const [text, setText] = useState("");
  useEffect(() => {
    const video = videoRef && videoRef.current;
    if (!on || !video || !cues) { setText(""); return undefined; }
    let raf = 0;
    let last = null;
    const tick = () => {
      const t = lineText(activeCue(cues, video.currentTime));
      if (t !== last) { last = t; setText(t); }
    };
    const loop = () => {
      tick();
      raf = !video.paused && !video.ended ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => { cancelAnimationFrame(raf); loop(); };
    const events = ["play", "playing", "pause", "seeking", "seeked", "ended", "loadedmetadata", "emptied"];
    events.forEach((e) => video.addEventListener(e, kick));
    kick();
    return () => {
      cancelAnimationFrame(raf);
      events.forEach((e) => video.removeEventListener(e, kick));
    };
  }, [videoRef, cues, on]);
  return on ? text : "";
}
